/**
 * BMT Companion DEV-ONLY harness — a localStorage-backed fake of the trainee
 * routes (/api/v2/training/bmt/*), so every BMT screen can be seen in a browser
 * with no backend, no Auth0 and no intake set up by LDT.
 *
 * Same shape as src/utils/voucher-harness.js: a toggle plus a state blob, gated
 * on non-production here AND in the store that consumes it (store/modules/bmt.js),
 * and driven from the /bmt-mock console, a route that production builds do not
 * contain.
 *
 * Fixtures are generated relative to today (Singapore time), so "check-in opens
 * at 6:00 pm" stays true however long this file sits in the repo. No real member
 * data: everything here is invented.
 */

export const BMT_HARNESS_KEY = 'bmtHarnessState';

export const BMT_HARNESS_SCENARIOS = Object.freeze({
  NOT_ENROLLED: 'not-enrolled',
  PENDING: 'pending',
  APPROVED: 'approved',
});

/** What the upcoming day's check-in window is doing in the approved scenario. */
export const BMT_HARNESS_CHECKIN = Object.freeze({
  NOT_YET: 'not-yet',
  OPEN: 'open',
  CLOSED: 'closed',
});

const isProd = () => process.env.VUE_APP_NODE_ENV === 'production';

const SGT_OFFSET_HOURS = 8;
const DAY_MS = 24 * 60 * 60 * 1000;

// ── Time helpers (Singapore calendar) ─────────────────────────────────────────

/** The Singapore calendar date `offset` days from today, as YYYY-MM-DD. */
export const sgDate = (offset = 0, now = new Date()) =>
  new Date(now.getTime() + SGT_OFFSET_HOURS * 3600 * 1000 + offset * DAY_MS)
    .toISOString()
    .slice(0, 10);

/** A Singapore wall-clock time on a Singapore date, as a UTC instant. */
export const sgInstant = (date, hhmm) => {
  const [y, m, d] = date.split('-').map(Number);
  const [h, min] = hhmm.split(':').map(Number);
  return new Date(Date.UTC(y, m - 1, d, h - SGT_OFFSET_HOURS, min)).toISOString();
};

const addMinutes = (iso, minutes) => new Date(Date.parse(iso) + minutes * 60 * 1000).toISOString();

// ── Fixture builders ──────────────────────────────────────────────────────────

const TOPICS = [
  ['Introduction to BMT', 'The Word of God'],
  ['Prayer', 'Serving in Church'],
  ['Generosity', 'Building Relationships'],
  ['Sharing Your Faith', 'Commissioning'],
];

let sessionSeq = 800;

function buildDay(date, { start, end, venue, topics, checkin = 'not-yet', statuses = [] }) {
  const startsAt = sgInstant(date, start);
  const endsAt = sgInstant(date, end);
  const opensAt = addMinutes(startsAt, -60);
  const closesAt = addMinutes(endsAt, 30);
  return {
    date,
    startsAt,
    endsAt,
    venue,
    checkin: { state: checkin, opensAt, closesAt },
    sessions: topics.map((title, i) => {
      sessionSeq += 1;
      const midpoint = addMinutes(startsAt, i * 75);
      const status = statuses[i] || { status: 'upcoming', attendedOn: null };
      return {
        id: sessionSeq,
        title,
        startsAt: midpoint,
        endsAt: addMinutes(midpoint, 70),
        ...status,
      };
    }),
  };
}

const INTAKES = {
  41: { label: 'BMT', start: '19:30', end: '22:00', venue: 'Hall 2', firstOffset: 12 },
  42: { label: 'BMT Youth', start: '14:00', end: '16:30', venue: 'Youth Hall', firstOffset: 13 },
};

/** Four weekly days, all still to come. */
function upcomingDays(intakeId) {
  const intake = INTAKES[intakeId];
  return TOPICS.map((topics, week) =>
    buildDay(sgDate(intake.firstOffset + week * 7), {
      start: intake.start,
      end: intake.end,
      venue: intake.venue,
      topics,
    }),
  );
}

/**
 * The approved trainee: one day behind them (both topics attended, one of them
 * made up on another date), the next day's check-in in whichever state the
 * console picked, and two more weeks to come.
 */
function approvedDays(checkin) {
  const intake = INTAKES[41];
  const pastDate = sgDate(-5);
  const past = buildDay(pastDate, {
    start: intake.start,
    end: intake.end,
    venue: intake.venue,
    topics: TOPICS[0],
    checkin: 'closed',
    statuses: [
      { status: 'attended', attendedOn: null },
      { status: 'attended', attendedOn: sgDate(-3) },
    ],
  });
  // Open or closed only makes sense today; not-yet is a couple of days out.
  const nextDate = checkin === BMT_HARNESS_CHECKIN.NOT_YET ? sgDate(2) : sgDate(0);
  const next = buildDay(nextDate, {
    start: intake.start,
    end: intake.end,
    venue: intake.venue,
    topics: TOPICS[1],
    checkin,
  });
  const later = [2, 3].map(i =>
    buildDay(sgDate(2 + (i - 1) * 7), {
      start: intake.start,
      end: intake.end,
      venue: intake.venue,
      topics: TOPICS[i],
    }),
  );
  return [past, next, ...later];
}

function homeworkFixtures(days) {
  const [first] = days;
  // The approved trainee's second day (today, when check-in is open or closed).
  const today = days[1]?.date === sgDate(0) ? days[1] : null;
  const firstSession = first?.sessions?.[0] || { id: 801, title: TOPICS[0][0] };
  const secondSession = first?.sessions?.[1] || { id: 802, title: TOPICS[0][1] };
  return [
    {
      id: 12,
      type: 'TICK',
      title: 'Ministry Application',
      linkUrl: 'https://www.heartofgodchurch.org',
      session: { id: firstSession.id, title: firstSession.title, date: first?.date || sgDate(-5) },
      submitted: false,
      submittedAt: null,
      text: null,
    },
    {
      id: 13,
      type: 'TEXT',
      title: 'What stood out to you about the Word of God?',
      linkUrl: null,
      session: {
        id: secondSession.id,
        title: secondSession.title,
        date: first?.date || sgDate(-5),
      },
      submitted: false,
      submittedAt: null,
      text: null,
    },
    ...(today
      ? [
          {
            id: 15,
            type: 'TICK',
            title: 'Pray for 10 minutes each day this week',
            linkUrl: null,
            session: { id: today.sessions[0].id, title: today.sessions[0].title, date: today.date },
            submitted: false,
            submittedAt: null,
            text: null,
          },
        ]
      : []),
    {
      id: 14,
      type: 'TICK',
      title: 'Read the BMT handbook, chapter 1',
      linkUrl: null,
      session: { id: firstSession.id, title: firstSession.title, date: first?.date || sgDate(-5) },
      submitted: true,
      submittedAt: sgInstant(sgDate(-4), '21:15'),
      text: null,
    },
  ];
}

// ── State ─────────────────────────────────────────────────────────────────────

const DEFAULT_STATE = {
  enabled: false,
  scenario: BMT_HARNESS_SCENARIOS.NOT_ENROLLED,
  checkin: BMT_HARNESS_CHECKIN.NOT_YET,
  needsCgCheck: true,
  /** The intake chosen on sign-up, or the fixture's own for pending/approved. */
  intakeId: 41,
  /** id → { submitted, submittedAt, text }, layered over the fixtures. */
  submissions: {},
};

function readState() {
  try {
    const raw = localStorage.getItem(BMT_HARNESS_KEY);
    return raw ? { ...DEFAULT_STATE, ...JSON.parse(raw) } : { ...DEFAULT_STATE };
  } catch {
    return { ...DEFAULT_STATE };
  }
}

function writeState(state) {
  try {
    localStorage.setItem(BMT_HARNESS_KEY, JSON.stringify(state));
  } catch {
    // Storage unavailable: the harness simply does not persist across reloads.
  }
  return state;
}

export const getBmtHarnessState = () => readState();

export const isBmtHarnessEnabled = () => !isProd() && readState().enabled === true;

/** Turn the harness on in a given scenario, discarding any earlier submissions. */
export function seedBmtHarness(scenario, options = {}) {
  return writeState({
    ...DEFAULT_STATE,
    ...options,
    enabled: true,
    scenario,
    submissions: {},
  });
}

export const updateBmtHarness = patch => writeState({ ...readState(), ...patch });

export const disableBmtHarness = () => writeState({ ...readState(), enabled: false });

// ── The fake API ──────────────────────────────────────────────────────────────

/** An axios-shaped error carrying the API's `{ statusCode, code, message }` body. */
export function harnessApiError(status, code, message = code) {
  const error = new Error(message);
  error.response = { status, data: { statusCode: status, code, message } };
  return error;
}

function daysFor(state) {
  // Same ids on every read, so homework and a check-in result can point at /me's sessions.
  sessionSeq = 800;
  if (state.scenario === BMT_HARNESS_SCENARIOS.APPROVED) return approvedDays(state.checkin);
  if (state.scenario === BMT_HARNESS_SCENARIOS.PENDING) return upcomingDays(state.intakeId);
  return [];
}

export function harnessMe(now = new Date()) {
  const state = readState();
  const enrolled = state.scenario !== BMT_HARNESS_SCENARIOS.NOT_ENROLLED;
  return {
    serverTime: now.toISOString(),
    enrolment: enrolled
      ? {
          id: 7,
          intakeId: state.intakeId,
          label: INTAKES[state.intakeId]?.label || 'BMT',
          status: state.scenario === BMT_HARNESS_SCENARIOS.APPROVED ? 'APPROVED' : 'PENDING',
        }
      : null,
    needsCgCheck: enrolled ? false : state.needsCgCheck,
    openIntakes: enrolled
      ? []
      : Object.keys(INTAKES).map(id => ({
          intakeId: Number(id),
          label: INTAKES[id].label,
          days: upcomingDays(Number(id)),
        })),
    days: daysFor(state),
  };
}

export function harnessEnrol({ intakeId, cgNote } = {}) {
  const state = readState();
  if (state.scenario !== BMT_HARNESS_SCENARIOS.NOT_ENROLLED) {
    if (state.intakeId === intakeId) return harnessMe();
    throw harnessApiError(409, 'ALREADY_ENROLLED', 'Already signed up for BMT.');
  }
  if (!INTAKES[intakeId]) {
    throw harnessApiError(409, 'INTAKE_NOT_OPEN', 'This intake is not open for sign-up.');
  }
  if (state.needsCgCheck && !cgNote) {
    throw harnessApiError(422, 'CG_NOTE_REQUIRED', 'Tell us about your Connect Group.');
  }
  // The API approves role I and above with a CG straight away; anyone it has to
  // ask about a CG waits for the BMT team.
  writeState({
    ...state,
    intakeId,
    scenario: state.needsCgCheck ? BMT_HARNESS_SCENARIOS.PENDING : BMT_HARNESS_SCENARIOS.APPROVED,
  });
  return harnessMe();
}

export function harnessHomework() {
  const state = readState();
  if (state.scenario === BMT_HARNESS_SCENARIOS.NOT_ENROLLED) {
    throw harnessApiError(403, 'NOT_ENROLLED', 'Not signed up for BMT.');
  }
  if (state.scenario === BMT_HARNESS_SCENARIOS.PENDING) {
    throw harnessApiError(403, 'NOT_APPROVED', 'Waiting for the BMT team.');
  }
  return homeworkFixtures(daysFor(state)).map(item => ({
    ...item,
    ...(state.submissions[item.id] || {}),
  }));
}

export function harnessSubmit(id, text) {
  const items = harnessHomework();
  const item = items.find(h => h.id === id);
  if (!item) throw harnessApiError(404, 'HOMEWORK_NOT_FOUND', 'Homework not found.');
  if (item.type === 'TEXT' && !text?.trim()) {
    throw harnessApiError(422, 'TEXT_REQUIRED', 'Text is required.');
  }
  const state = readState();
  writeState({
    ...state,
    submissions: {
      ...state.submissions,
      [id]: {
        submitted: true,
        submittedAt: new Date().toISOString(),
        text: item.type === 'TEXT' ? text : null,
      },
    },
  });
  return harnessHomework();
}

export function harnessUnsubmit(id) {
  const state = readState();
  writeState({
    ...state,
    submissions: {
      ...state.submissions,
      [id]: { submitted: false, submittedAt: null, text: null },
    },
  });
  return harnessHomework();
}

// ── Check-in results (the /bmt/check-in screen) ───────────────────────────────

/** The outcomes the console can show, as `bmt.lastCheckIn` would hold them. */
export const BMT_HARNESS_RESULTS = Object.freeze({
  CHECKED_IN: 'checked-in',
  CHECKED_IN_PENDING: 'checked-in-pending',
  CHECKED_IN_NO_TIME: 'checked-in-no-time',
  ALREADY: 'already',
  ALREADY_NO_TIME: 'already-no-time',
  NOT_OPEN: 'not-open',
  CLOSED: 'closed',
  ERROR: 'error',
});

/**
 * A `bmt.lastCheckIn` fixture for one outcome, against today's fixture day
 * (the approved trainee's, so its homework lines up), or null for none.
 */
export function harnessCheckInResult(kind, now = new Date()) {
  const today = sgDate(0, now);
  sessionSeq = 800;
  const day = approvedDays(BMT_HARNESS_CHECKIN.OPEN).find(d => d.date === today);
  const sessions = (day?.sessions || []).map(({ id, title }) => ({ id, title }));
  const checkedInAt = addMinutes(day?.startsAt || now.toISOString(), -12);
  const checkIn = (overrides = {}) => ({
    alreadyCheckedIn: false,
    date: today,
    sessions,
    enrolmentStatus: 'APPROVED',
    checkedInAt,
    ...overrides,
  });
  const success = (code, data) => ({
    status: 'success',
    code,
    display: { title: 'Checked in' },
    checkIn: data,
    opensAt: null,
  });
  const failure = (code, message, opensAt = null) => ({
    status: 'error',
    code,
    display: { title: 'Check-in', message },
    checkIn: null,
    opensAt,
  });
  switch (kind) {
    case BMT_HARNESS_RESULTS.CHECKED_IN:
      return success('CHECKED_IN', checkIn());
    case BMT_HARNESS_RESULTS.CHECKED_IN_PENDING:
      return success('CHECKED_IN', checkIn({ enrolmentStatus: 'PENDING' }));
    case BMT_HARNESS_RESULTS.CHECKED_IN_NO_TIME:
      return success('CHECKED_IN', checkIn({ checkedInAt: undefined }));
    case BMT_HARNESS_RESULTS.ALREADY:
      return success('ALREADY_CHECKED_IN', checkIn({ alreadyCheckedIn: true }));
    case BMT_HARNESS_RESULTS.ALREADY_NO_TIME:
      return success(
        'ALREADY_CHECKED_IN',
        checkIn({ alreadyCheckedIn: true, checkedInAt: undefined }),
      );
    case BMT_HARNESS_RESULTS.NOT_OPEN:
      return failure(
        'CHECKIN_NOT_OPEN',
        'Check-in is not open yet.',
        addMinutes(now.toISOString(), 25),
      );
    case BMT_HARNESS_RESULTS.CLOSED:
      return failure('CHECKIN_CLOSED', 'Check-in has closed.');
    case BMT_HARNESS_RESULTS.ERROR:
      return failure('INVALID_QR', 'This QR code is not valid.');
    default:
      return null;
  }
}
